import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import PersonAddAltOutlined from "@mui/icons-material/PersonAddAltOutlined";
import {
  Autocomplete,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormData } from "@types";
import { useEffect, useRef, useState } from "react";
import { BaseUser } from "../../../../../../../stores/models";
import { includeAuthorInApprovers } from "../helpers";

const ADD_NEW_USER_OPTION = {
  id: "__ADD_NEW_USER__",
  firstName: "Add new user",
  lastName: "",
  email: "",
} as BaseUser;

export type SearchAssigneesProps = {
  users: BaseUser[];
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  isAuthor: boolean;
  currentVersionAuthor?: BaseUser | null;
  onOpenInviteModal: () => void;
  handleAuthorChange?: (newValue: BaseUser | null) => void;
  handleApproversChange?: (newValue: (BaseUser | null)[] | null) => void;
  currentUser: BaseUser | null;
};

export const SearchAssignees = ({
  users,
  formData,
  setFormData,
  isAuthor = false,
  onOpenInviteModal,
  handleAuthorChange,
  handleApproversChange,
  currentUser,
}: SearchAssigneesProps) => {
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Prefill author with current user
  useEffect(() => {
    if (currentUser && !formData.selectedAuthor) {
      setFormData((prev) => ({
        ...prev,
        selectedAuthor: currentUser,
      }));
    }
  }, [currentUser, formData.selectedAuthor, setFormData]);

  // Prefill approvers with previous version author if it exists
  useEffect(() => {
    if (!isAuthor && formData.selectedUsers.length === 0) {
      setFormData((prev) => ({
        ...prev,
        selectedUsers: includeAuthorInApprovers(
          prev.selectedUsers,
          prev.selectedAuthor
        ),
      }));
    }
  }, []);

  const shouldHideOption = (option: BaseUser | null): boolean => {
    if (!option) return false;
    if (option.id === ADD_NEW_USER_OPTION.id) return false;
    if (isAuthor) {
      return formData.selectedAuthor?.id === option.id;
    }
    return formData.selectedUsers.some((u) => u?.id === option.id);
  };

  // Hide clear button when only author is selected (cannot be removed)
  const onlyAuthorSelected = !isAuthor && formData.selectedUsers.length === 1;

  const isUserNotInOptions = (selectedUser: BaseUser | null): boolean => {
    return (
      !!selectedUser &&
      !users.some((optionUser) => optionUser.id === selectedUser.id)
    );
  };

  // Include selected users that might not be in the `users` prop (e.g., newly invited users, protected users)
  const selectedUsersNotInOptions = isAuthor
    ? formData.selectedAuthor && isUserNotInOptions(formData.selectedAuthor)
      ? [formData.selectedAuthor]
      : []
    : formData.selectedUsers.filter(isUserNotInOptions);

  const allOptions = [
    ADD_NEW_USER_OPTION,
    ...users,
    ...selectedUsersNotInOptions,
  ];

  return (
    <div key={`search-assignees`} className="w-full">
      <Autocomplete
        disablePortal
        multiple={!isAuthor}
        options={allOptions}
        fullWidth
        disableClearable={onlyAuthorSelected}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label={isAuthor ? `Select Author` : `Select Approvers`}
            sx={{
              "& .MuiInputBase-root.MuiFilledInput-root": {
                ...(!isAuthor && { paddingTop: "15px !important" }),
              },
            }}
            autoComplete="off"
          />
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        value={isAuthor ? formData.selectedAuthor : formData.selectedUsers}
        renderTags={(value, getTagProps) => {
          if (isAuthor) return null;
          return value.map((option, index) => {
            const isCurrentAuthor = formData.selectedAuthor?.id === option?.id;
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Tooltip
                title={isCurrentAuthor && "Author must approve"}
                key={`tooltip-${option?.id}`}
              >
                <Chip
                  key={key}
                  {...tagProps}
                  label={`${option?.firstName} ${option?.lastName}`}
                  onDelete={isCurrentAuthor ? undefined : tagProps.onDelete}
                />
              </Tooltip>
            );
          });
        }}
        onChange={(_, newValue) =>
          isAuthor
            ? handleAuthorChange?.(newValue as BaseUser)
            : handleApproversChange?.(newValue as BaseUser[])
        }
        renderOption={(props, option) => {
          if (option?.id === ADD_NEW_USER_OPTION.id) {
            const { onClick, ...restProps } = props;
            return (
              <li
                {...restProps}
                key={`li-${option.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  onOpenInviteModal();
                  setAutocompleteOpen(false);
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="flex gap-3">
                  <div className="flex items-center justify-center">
                    <PersonAddAltOutlined />
                  </div>
                  <div>
                    <Typography variant="body1" fontWeight="500">
                      <span>{option.firstName}</span>
                    </Typography>
                  </div>
                </div>
              </li>
            );
          }

          // Hide selected users visually
          // This is needed to support the MUI Autocomplete component
          if (shouldHideOption(option)) {
            return null;
          }
          return (
            <li {...props} key={`li-${option?.id}`}>
              <div className="flex gap-3">
                <div className="flex items-center justify-center">
                  <PermIdentityIcon />
                </div>
                <div>
                  <Typography variant="body1" fontWeight="500">
                    <span>{`${option?.firstName} ${option?.lastName}`}</span>
                  </Typography>
                  <div>{option?.email}</div>
                </div>
              </div>
            </li>
          );
        }}
        ListboxProps={{ style: { maxHeight: 200 } }}
        getOptionLabel={(option) => {
          if (option?.id === ADD_NEW_USER_OPTION.id) {
            return option.firstName || "";
          }
          return (
            `${option?.firstName || ""} ${option?.lastName || ""}`.trim() || ""
          );
        }}
      />
    </div>
  );
};

export default SearchAssignees;
